import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import React, { useMemo, useEffect, useState } from 'react'

import { useLazyGetAccountRouteStopsQuery } from 'api/accounts/accountRouting'
import MapPlaceholder from 'components/common/address/MapPlaceholder'
import RouteDetails from 'components/customer-details/content/routing/RouteDetails'
import { getServiceRecurrenceText } from 'components/locations/LocationsDataGrid/RenderLocationDetailPanelContent/ConfiguredServiceCard'
import { selectLatitude, selectLongitude } from 'data/address/addressSelectors'
import { WORK_ORDER_STATUS_DISPLAY } from 'settings/constants/service'
import T from 'T'
import { formatDateToBEFormatDateFns, createDateFromString } from 'utils/date'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { combineAddressInfo } from 'utils/helper'
import { getFeaturePointFromCoordsObj } from 'utils/map'
import { isValidLatitudeRange, isValidLongitudeRange } from 'utils/validations'
import { stopStatus } from 'utils/workOrderStatus'

import WorkOrderLocationMap from './WorkOrderLocationMap'

// The route-stop fields this tab prefers over the work order's own.
type RoutingFields = {
  sequence?: number | null
  businessLine?: string | null
  materialType?: string | null
  materialColor?: string | null
  routeId?: string | null
  routeName?: string | null
  routeColor?: string | null
}

type RouteStop = RoutingFields & {
  workOrderId?: string
  daysOfWeek?: string | null
  currentStop?: number | null
  stopCount?: number | null
  action?: string | null
}

type RoutingWorkOrder = RoutingFields & {
  workOrderId?: string
  workOrderNumber?: string
  workOrderStatus?: string
  completionStatus?: string
  intentStatus?: string
  pricedServiceType?: string
  serviceName?: string
  serviceDate?: string
  eventActionType?: string
  measureUnit?: string
  measureVolume?: number
  tags?: React.ComponentProps<typeof RouteDetails>['tags']
}

type Props = {
  workOrder?: RoutingWorkOrder
  accountId?: string
}

const WorkOrderRoutingTab: React.FC<Props> = ({ workOrder = {}, accountId = '' }) => {
  const [getAccountRouteStops, { data: routeStopsData, isFetching, isSuccess }] = useLazyGetAccountRouteStopsQuery()
  const [isMapReady, setIsMapReady] = useState(false)

  // Extract work order fields
  const {
    workOrderId = '',
    workOrderNumber = '',
    intentStatus = '',
    pricedServiceType = '',
    serviceName = '',
    serviceDate = '',
    sequence = 0,
    eventActionType = '',
    measureUnit = '',
    measureVolume = 0,
    tags = [],
  } = workOrder
  const status = stopStatus(workOrder) ?? ''

  // Find the matching route stop for this work order
  const routeStop = useMemo<RouteStop>(() => {
    const stops: RouteStop[] = routeStopsData?.stops ?? []
    return stops.find(stop => stop.workOrderId === workOrderId) || {}
  }, [routeStopsData, workOrderId])

  // Use route stop data for location, fallback to work order data
  const latitude = selectLatitude(routeStop) || selectLatitude(workOrder)
  const longitude = selectLongitude(routeStop) || selectLongitude(workOrder)
  const hasValidLatLong = isValidLatitudeRange(latitude) && isValidLongitudeRange(longitude)
  // The route stop is fetched on demand; Work Orders Review lists work orders without their sequence.
  const stopSequence = routeStop.sequence ?? sequence
  const position = stopSequence || 'U'

  // Get additional data from route stop, fallback to work order data
  const businessLine = routeStop.businessLine || workOrder.businessLine || ''
  const materialType = routeStop.materialType || workOrder.materialType || ''
  const materialColor = routeStop.materialColor || workOrder.materialColor || ''
  const daysOfWeek = routeStop.daysOfWeek ?? ''
  const currentStop = routeStop.currentStop ?? 0
  const stopCount = routeStop.stopCount ?? 0
  const routeId = routeStop.routeId || workOrder.routeId || ''
  const routeName = routeStop.routeName || workOrder.routeName || ''
  const routeColor = routeStop.routeColor || workOrder.routeColor || ''
  const action = routeStop.action || eventActionType

  // Derive address string
  const addressStr = useMemo(() => combineAddressInfo(workOrder), [workOrder])

  // Derive recurrence text
  const recurrence = useMemo(
    () => getServiceRecurrenceText({ ...workOrder, serviceType: pricedServiceType }),
    [workOrder, pricedServiceType]
  )

  // Derive measure label
  const measure = useMemo(() => {
    if (!measureVolume && !measureUnit) return ''
    const unit = getMeasureUnit(measureUnit)
    return `${measureVolume} ${unit}`.trim()
  }, [measureVolume, measureUnit])

  // Fallback: derive day of week from service date
  const derivedDaysOfWeek = useMemo(() => {
    if (daysOfWeek) return daysOfWeek
    if (!serviceDate) return ''
    const dayAbbreviations = ['S', 'M', 'T', 'W', 'R', 'F', 'U']
    const date = createDateFromString(serviceDate)
    if (!date) return ''
    const dayIndex = date.getDay()
    return dayAbbreviations[dayIndex] || ''
  }, [daysOfWeek, serviceDate])

  // Create features for the map marker - memoized so it's ready when map renders
  const features = useMemo(() => {
    if (!hasValidLatLong) return []

    const marker = WORK_ORDER_STATUS_DISPLAY[status]?.marker
    const markerTextColor = WORK_ORDER_STATUS_DISPLAY[status]?.markerTextColor
    const currentMarkerFeature = getFeaturePointFromCoordsObj({
      latitude,
      longitude,
      properties: {
        position,
        icon: position === 'U' ? 'to-be-assigned-marker' : marker,
        'text-color': markerTextColor,
      },
    })
    return [currentMarkerFeature]
  }, [hasValidLatLong, latitude, longitude, status, position])

  // Fetch route stops when component mounts
  useEffect(() => {
    if (accountId && serviceDate && workOrderId) {
      const formattedDate = formatDateToBEFormatDateFns(createDateFromString(serviceDate))
      getAccountRouteStops({ accountId, serviceDate: formattedDate, workOrderId })
    }
  }, [accountId, serviceDate, workOrderId, getAccountRouteStops])

  // Set map ready flag once we have valid features
  useEffect(() => {
    if (hasValidLatLong && features.length > 0 && !isFetching) {
      setIsMapReady(true)
    }
  }, [hasValidLatLong, features, isFetching])

  const isLoading = isFetching || (!isSuccess && !isMapReady)

  if (!accountId) {
    return <Alert severity="info">{T.LOCATION_MAP_NOT_AVAILABLE}</Alert>
  }

  return (
    <Box p={2}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box height={250}>
            {isLoading && <Skeleton height={250} variant="rectangular" />}
            {!isLoading && isMapReady && hasValidLatLong && (
              <WorkOrderLocationMap features={features} latitude={latitude} longitude={longitude} />
            )}
            {!isLoading && !hasValidLatLong && <MapPlaceholder />}
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          {isLoading ? (
            <Box>
              <Skeleton height={32} width="60%" />
              <Skeleton height={32} width="80%" />
              <Skeleton height={32} width="70%" />
              <Skeleton height={32} width="50%" />
            </Box>
          ) : (
            <RouteDetails
              action={action}
              addressStr={addressStr}
              businessLine={businessLine}
              currentStop={currentStop}
              daysOfWeek={derivedDaysOfWeek}
              intentStatus={intentStatus}
              materialColor={materialColor}
              materialType={materialType}
              measure={measure}
              measureUnit={measureUnit}
              recurrence={recurrence}
              routeColor={routeColor}
              routeId={routeId}
              routeName={routeName}
              sequence={stopSequence ?? undefined}
              serviceDate={serviceDate}
              serviceName={serviceName}
              serviceType={pricedServiceType}
              stopCount={stopCount}
              tags={tags}
              workOrderId={workOrderId}
              workOrderNumber={workOrderNumber}
              workOrderStatus={status}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

export default WorkOrderRoutingTab
